import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import {
  AccountState,
  CLIENT_CAPABILITY_HELPERS,
  PADDIQ_PERMISSIONS,
  PERMISSION_DOMAINS,
  PERMISSION_ID_PREFIX,
  PERMISSION_MATRIX_ROLES,
  PERMISSION_STORAGE_MODEL,
  PermissionEnforcementLayer,
  PermissionGrant,
  UserRole,
  countPermissionGrantsForRole,
  formatUserRoleCode,
  formatUserRoleLabel,
  permissionGrantColor,
  permissionGrantLabel,
} from '@kangaroo/shared';
import { colors } from '@kangaroo/ui';

import {
  canCreateTeamWorkspace,
  canManageDataFiles,
  canManageDrivers,
  canManageKarts,
  canManageTeam,
  canUpdateTelemetrySessionContext,
  canUseTracksideImport,
  canViewFleet,
  canViewTeamMembers,
} from '../../lib/roles';
import {
  canSelectAnyTeamDriver,
  isSelfScopedDriverRole,
} from '../../lib/driverAccess';
import { useAuthStore } from '../../store/authStore';

const panelSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.2,
  boxShadow: '0 0 0 1px rgba(255,255,255,0.02)',
};

const matrixHeadCellSx = {
  fontSize: '0.62rem',
  fontWeight: 850,
  color: 'text.secondary',
  py: 0.75,
  px: 0.75,
  whiteSpace: 'nowrap',
};

const LIVE_CLIENT_HELPERS: Array<{
  helper: string;
  sourceFile: string;
  evaluate: (role: UserRole) => boolean;
}> = [
  { helper: 'canManageTeam', sourceFile: 'roles.ts', evaluate: canManageTeam },
  { helper: 'canManageDrivers', sourceFile: 'roles.ts', evaluate: canManageDrivers },
  { helper: 'canViewTeamMembers', sourceFile: 'roles.ts', evaluate: canViewTeamMembers },
  { helper: 'canManageKarts', sourceFile: 'roles.ts', evaluate: canManageKarts },
  { helper: 'canViewFleet', sourceFile: 'roles.ts', evaluate: canViewFleet },
  { helper: 'canManageDataFiles', sourceFile: 'roles.ts', evaluate: canManageDataFiles },
  { helper: 'canUseTracksideImport', sourceFile: 'roles.ts', evaluate: canUseTracksideImport },
  { helper: 'canUpdateTelemetrySessionContext', sourceFile: 'roles.ts', evaluate: canUpdateTelemetrySessionContext },
  { helper: 'canCreateTeamWorkspace', sourceFile: 'roles.ts', evaluate: canCreateTeamWorkspace },
  { helper: 'canSelectAnyTeamDriver', sourceFile: 'driverAccess.ts', evaluate: canSelectAnyTeamDriver },
  { helper: 'isDriverSelectionLocked (via isSelfScopedDriverRole)', sourceFile: 'driverAccess.ts', evaluate: isSelfScopedDriverRole },
];

function GrantChip({ grant }: { grant: PermissionGrant }) {
  return (
    <Chip
      label={permissionGrantLabel(grant)}
      size="small"
      color={permissionGrantColor(grant)}
      sx={{ height: 22, minWidth: 28, fontSize: '0.72rem', fontWeight: 800, '& .MuiChip-label': { px: 0.75 } }}
    />
  );
}

function BoolChip({ value }: { value: boolean }) {
  return (
    <Chip
      label={value ? 'true' : 'false'}
      size="small"
      color={value ? 'success' : 'default'}
      variant={value ? 'filled' : 'outlined'}
      sx={{ height: 22, minWidth: 42, fontSize: '0.62rem', fontWeight: 700, '& .MuiChip-label': { px: 0.65 } }}
    />
  );
}

type MatrixRow = {
  key: string;
  primary: string;
  secondary?: string;
  values: Array<{ role: UserRole; grant: PermissionGrant }>;
  renderValue: (grant: PermissionGrant) => ReactNode;
};

function MatrixTable({ rowHeader, rows }: { rowHeader: string; rows: MatrixRow[] }) {
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...matrixHeadCellSx, minWidth: 220, bgcolor: 'background.paper' }}>{rowHeader}</TableCell>
            {PERMISSION_MATRIX_ROLES.map((role) => (
              <TableCell key={role} sx={{ ...matrixHeadCellSx, textAlign: 'center', bgcolor: 'background.paper' }}>
                {formatUserRoleCode(role)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} hover>
              <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: 280 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.35 }}>{row.primary}</Typography>
                {row.secondary ? (
                  <Typography sx={{ mt: 0.25, fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1.35, wordBreak: 'break-word' }}>
                    {row.secondary}
                  </Typography>
                ) : null}
              </TableCell>
              {row.values.map(({ role, grant }) => (
                <TableCell key={`${row.key}-${role}`} align="center" sx={{ py: 0.5 }}>
                  {row.renderValue(grant)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function RolesPermissionsSettings() {
  const user = useAuthStore((state) => state.user);
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return PADDIQ_PERMISSIONS.filter((permission) => {
      if (domainFilter !== 'all' && permission.domain !== domainFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return (
        permission.id.toLowerCase().includes(query)
        || permission.displayName.toLowerCase().includes(query)
        || permission.clientHelper?.toLowerCase().includes(query)
        || permission.apiRoute?.toLowerCase().includes(query)
      );
    });
  }, [domainFilter, search]);

  const currentRoleCounts = user ? countPermissionGrantsForRole(user.role) : null;

  return (
    <Stack spacing={1.25}>
      <Box sx={[panelSx, {p: 1.35 }]}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18, color: colors.primary[200], mt: 0.15 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 900 }}>Roles & permissions</Typography>
            <Typography sx={{ mt: 0.35, fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.45 }}>
              Read-only reference matrix synced with @kangaroo/shared and client helper functions.
            </Typography>
          </Box>
        </Stack>
        {user ? (
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', mt: 1 }}>
            <Chip
              label={`Your role: ${formatUserRoleLabel(user.role)} (${formatUserRoleCode(user.role)})`}
              size="small"
              sx={{ height: 24, fontSize: '0.66rem', fontWeight: 800, bgcolor: 'rgba(124,58,237,0.14)', color: colors.primary[200] }}
            />
            {currentRoleCounts ? (
              <Chip
                label={`${currentRoleCounts.allowed} allow · ${currentRoleCounts.scoped} scoped · ${currentRoleCounts.denied} deny`}
                size="small"
                sx={{ height: 24, fontSize: '0.66rem', fontWeight: 700 }}
              />
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.75 }}>How permission sets are stored</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5, mb: 1 }}>
          {PERMISSION_STORAGE_MODEL.summary}
        </Typography>
        <Stack spacing={0.75}>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>Role field:</strong> {PERMISSION_STORAGE_MODEL.roleField}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>Role storage:</strong> {PERMISSION_STORAGE_MODEL.roleStorage}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>Permission set storage:</strong> {PERMISSION_STORAGE_MODEL.permissionSetStorage}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>Client evaluation:</strong> {PERMISSION_STORAGE_MODEL.clientEvaluation}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>API evaluation:</strong> {PERMISSION_STORAGE_MODEL.apiEvaluation}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.45 }}>
            <strong>Future:</strong> {PERMISSION_STORAGE_MODEL.futureJwt}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', mt: 1.1 }}>
          {Object.values(AccountState).map((state) => (
            <Chip key={state} label={state} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.62rem', fontWeight: 700 }} />
          ))}
        </Stack>
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.75 }}>UserRole enum</Typography>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={matrixHeadCellSx}>Enum value</TableCell>
                <TableCell sx={matrixHeadCellSx}>Matrix code</TableCell>
                <TableCell sx={matrixHeadCellSx}>Label</TableCell>
                <TableCell sx={matrixHeadCellSx} align="right">Allow</TableCell>
                <TableCell sx={matrixHeadCellSx} align="right">Scoped</TableCell>
                <TableCell sx={matrixHeadCellSx} align="right">Deny</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PERMISSION_MATRIX_ROLES.map((role) => {
                const counts = countPermissionGrantsForRole(role);
                return (
                  <TableRow key={role} hover>
                    <TableCell sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>{role}</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700 }}>{formatUserRoleCode(role)}</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem' }}>{formatUserRoleLabel(role)}</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem' }} align="right">{counts.allowed}</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem' }} align="right">{counts.scoped}</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem' }} align="right">{counts.denied}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.35 }}>Client capability helpers (live)</Typography>
        <MatrixTable
          rowHeader="Helper"
          rows={LIVE_CLIENT_HELPERS.map((entry) => ({
            key: entry.helper,
            primary: entry.helper,
            secondary: entry.sourceFile,
            values: PERMISSION_MATRIX_ROLES.map((role) => ({
              role,
              grant: entry.evaluate(role) ? PermissionGrant.ALLOW : PermissionGrant.DENY,
            })),
            renderValue: (grant) => <BoolChip value={grant === PermissionGrant.ALLOW} />,
          }))}
        />
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.35 }}>Documented client capabilities</Typography>
        <MatrixTable
          rowHeader="Capability"
          rows={CLIENT_CAPABILITY_HELPERS.map((capability) => ({
            key: capability.id,
            primary: `${capability.helper}()`,
            secondary: `${capability.description} · ${capability.permissionIds.join(', ')}`,
            values: PERMISSION_MATRIX_ROLES.map((role) => ({
              role,
              grant: capability.roles[role] ? PermissionGrant.ALLOW : PermissionGrant.DENY,
            })),
            renderValue: (grant) => <BoolChip value={grant === PermissionGrant.ALLOW} />,
          }))}
        />
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1, alignItems: { sm: 'center' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>Permission matrix</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.25 }}>
              ✓ allow · ◐ scoped · — deny
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)}>
                <MenuItem value="all">All domains</MenuItem>
                {PERMISSION_DOMAINS.map((domain) => (
                  <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search permissions…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 180, '& .MuiInputBase-root': { fontSize: '0.72rem', minHeight: 36 } }}
            />
          </Stack>
        </Stack>
        <MatrixTable
          rowHeader="Permission"
          rows={filteredPermissions.map((permission) => ({
            key: permission.id,
            primary: permission.displayName,
            secondary: [permission.id, permission.enforcement, permission.clientHelper, permission.apiRoute, permission.scopeNotes].filter(Boolean).join(' · '),
            values: PERMISSION_MATRIX_ROLES.map((role) => ({ role, grant: permission.roles[role] })),
            renderValue: (grant) => <GrantChip grant={grant} />,
          }))}
        />
      </Box>

      <Box sx={[panelSx, {p: 1.35 }]}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.75 }}>Types & constants</Typography>
        <Stack spacing={0.5}>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            PermissionGrant: allow | deny | scoped
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            PermissionEnforcementLayer: {Object.values(PermissionEnforcementLayer).join(' | ')}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            AccountState: {Object.values(AccountState).join(' | ')}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            UserRole: {PERMISSION_MATRIX_ROLES.join(' | ')}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            PERMISSION_ID_PREFIX: &quot;{PERMISSION_ID_PREFIX}&quot;
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', lineHeight: 1.5 }}>
            PADDIQ_PERMISSIONS: {PADDIQ_PERMISSIONS.length} PermissionDefinition entries
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
